import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Loader2 } from 'lucide-react'
import Sidebar from '../components/layout/Sidebar'
import SetupChatShell from '../components/setup/SetupChatShell'
import SetupMessageTimeline from '../components/setup/SetupMessageTimeline'
import SetupComposer from '../components/setup/SetupComposer'
import BriefProgressPanel from '../components/setup/BriefProgressPanel'
import { briefAPI, meetingsAPI } from '../services/api'
import { useAuth } from '../context/AuthContext'
import { useToast } from '../components/common/Toast'
import { isExternalJoinEnabled } from '../config/externalJoinBeta'

/**
 * Step 3 of the Teams join-external wizard: the "brief your chief of staff"
 * chat. Reuses the Leader Proxy chat components (SetupChatShell,
 * SetupMessageTimeline, SetupComposer, BriefProgressPanel) and the backend
 * brief-capture API (briefAPI.turn / briefAPI.complete).
 *
 * Differences vs. LeaderProxyPage:
 *  - No meeting picker — the meetingId is in the URL (handed off from Step 2).
 *  - Custom turn-0 welcome message framed for "joining someone else's meeting".
 *  - Done CTA publishes the draft (sets isDraft: false) and navigates to the
 *    meeting detail page. Brief.complete is best-effort (server hard-gates on
 *    >= 1 goal, so we skip it silently if the user's brief is sparse).
 *  - Skip CTA publishes without calling complete — brief stays as captured
 *    but not finalized.
 *
 * Backend: zero changes. POST /api/meetings/:meetingId/brief/turn already
 * accepts any meeting_join_kind including assistant_external.
 *
 * See docs/teams_join_setup_redesign.md.
 */

const STORAGE_KEY_PREFIX = 'rithmo_external_brief_'

function welcomeMessage(firstName) {
  const greeting = firstName ? `Hey ${firstName}!` : 'Hey!'
  return [
    `${greeting} You're about to join this meeting and I'll be sitting in with you as your chief of staff.`,
    '',
    `Anything I should know before we go in? What you're hoping to walk away with, who matters most in the room, or anything to watch for?`,
    '',
    `*If you'd rather just dive in, hit "Add Rithmo without briefing" below.*`,
  ].join('\n')
}

export default function ExternalMeetingBriefPage() {
  const navigate = useNavigate()
  const { meetingId } = useParams()
  const { user } = useAuth()
  const toast = useToast()

  const storageKey = meetingId ? `${STORAGE_KEY_PREFIX}${meetingId}` : null

  // Hydrate messages from localStorage so a page reload doesn't lose context.
  const [messages, setMessages] = useState(() => {
    if (!storageKey) return []
    try {
      const raw = localStorage.getItem(storageKey)
      if (raw) return JSON.parse(raw)
    } catch { /* fall through */ }
    return []
  })

  const [fieldCompleteness, setFieldCompleteness] = useState(null)
  const [brief, setBrief] = useState(null)
  const [sidebandAgenda, setSidebandAgenda] = useState(null)
  const [isSendingTurn, setIsSendingTurn] = useState(false)
  const [isFinalizing, setIsFinalizing] = useState(false)
  const [meetingLoaded, setMeetingLoaded] = useState(false)
  const initRef = useRef(false)

  // Beta gate
  useEffect(() => {
    if (user && !isExternalJoinEnabled(user.email)) {
      navigate('/dashboard', { replace: true })
    }
  }, [user, navigate])

  // Load the draft meeting + any existing brief
  useEffect(() => {
    if (!meetingId || initRef.current) return
    initRef.current = true
    Promise.all([
      meetingsAPI.get(meetingId).catch(() => null),
      briefAPI.get(meetingId).catch(() => null),
    ]).then(([meetingRes, briefRes]) => {
      const m = meetingRes?.data ?? meetingRes
      if (!m) {
        toast.error('Meeting not found', 'Start over from the dashboard.')
        navigate('/dashboard', { replace: true })
        return
      }
      setMeetingLoaded(true)
      const existingBrief = briefRes?.brief ?? briefRes?.data?.brief ?? null
      if (existingBrief) setBrief(existingBrief)
      // First-load welcome
      if (messages.length === 0) {
        setMessages([
          {
            id: 'brief-welcome',
            role: 'assistant',
            content: welcomeMessage(user?.firstName),
            timestamp: Date.now(),
          },
        ])
      }
    })
  }, [meetingId]) // eslint-disable-line react-hooks/exhaustive-deps

  // Persist messages so reloads don't lose context
  useEffect(() => {
    if (!storageKey) return
    try { localStorage.setItem(storageKey, JSON.stringify(messages)) } catch { /* non-fatal */ }
  }, [messages, storageKey])

  const handleSendMessage = useCallback(async (content, { fromVoice = false } = {}) => {
    if (!meetingId || !content?.trim()) return
    const trimmed = content.trim()

    const userMsg = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: trimmed,
      timestamp: Date.now(),
    }
    const thinkingId = `thinking-${Date.now()}`
    setMessages((prev) => [
      ...prev,
      userMsg,
      { id: thinkingId, role: 'assistant', content: '', timestamp: Date.now(), isTyping: true },
    ])
    setIsSendingTurn(true)
    try {
      const priorMessages = messages
        .filter((m) => m.id !== 'brief-welcome' && !m.id?.startsWith('thinking-'))
        .map((m) => ({ role: m.role, content: m.content }))
        .slice(-10)
      const sourceType = fromVoice ? 'voice_transcript' : 'chat_text'
      const result = await briefAPI.turn(meetingId, trimmed, sourceType, priorMessages, undefined)
      const payload = result?.data ?? result

      if (payload?.assistantMessage) {
        const base = payload.assistantMessage
        const clarification = payload?.clarificationQuestion?.question
        const combined = clarification ? `${base}\n\n*${clarification}*` : base
        setMessages((prev) => [
          ...prev.filter((m) => m.id !== thinkingId),
          { id: `assistant-${Date.now()}`, role: 'assistant', content: combined, timestamp: Date.now() },
        ])
      } else {
        setMessages((prev) => prev.filter((m) => m.id !== thinkingId))
      }

      setFieldCompleteness(payload?.fieldCompleteness || null)
      setBrief(payload?.brief || null)
      if (payload?.proposedAgenda) setSidebandAgenda(payload.proposedAgenda)
    } catch (err) {
      setMessages((prev) =>
        prev
          .filter((m) => m.id !== thinkingId)
          .map((m) =>
            m.id === userMsg.id
              ? { ...m, error: err?.message || 'Hmm, that didn’t go through.' }
              : m,
          ),
      )
      toast.error('Could not reach Rithmo', err?.message ?? 'Please try again.')
    } finally {
      setIsSendingTurn(false)
    }
  }, [meetingId, messages, toast])

  const handleAddRithmo = useCallback(async ({ withBrief }) => {
    if (!meetingId) return
    setIsFinalizing(true)
    try {
      // If the user actually filled out a brief (at least one goal), finalize
      // it server-side so it translates to governanceConfig + Layer 3.
      if (withBrief && fieldCompleteness?.meetingGoals === 'populated') {
        try {
          await briefAPI.complete(meetingId)
        } catch (err) {
          // Best-effort — if the brief doesn't meet the hard gate, just publish.
          console.warn('briefAPI.complete skipped:', err?.message)
        }
      }
      // Flip draft → live. Bot dispatch happens server-side.
      await meetingsAPI.update(meetingId, { isDraft: false })
      // Clear persisted chat history on success
      if (storageKey) {
        try { localStorage.removeItem(storageKey) } catch { /* non-fatal */ }
      }
      navigate(`/meeting/${meetingId}`)
    } catch (err) {
      toast.error('Could not add Rithmo', err?.message ?? 'Please try again.')
    } finally {
      setIsFinalizing(false)
    }
  }, [meetingId, fieldCompleteness, navigate, toast, storageKey])

  const hasUserMessage = useMemo(
    () => messages.some((m) => m.role === 'user'),
    [messages],
  )

  if (!meetingLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-8 h-8 animate-spin text-[#16c1b2]" />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Sidebar />

      <main className="lg:ml-[280px] relative overflow-hidden p-4 pt-20 lg:p-8 lg:pt-8">
        <div className="pointer-events-none absolute inset-x-0 top-0 h-80 bg-[radial-gradient(circle_at_top,rgba(22,193,178,0.12),transparent_62%)]" />
        <div className="pointer-events-none absolute right-0 top-20 h-64 w-64 rounded-full bg-[#e4faf8] blur-3xl opacity-60" />

        <div className="relative mx-auto max-w-6xl">
          <div className="mb-6 flex items-center justify-between gap-3">
            <Link
              to={`/join-external?edit=${encodeURIComponent(meetingId)}`}
              className="inline-flex items-center gap-2 text-sm font-medium text-gray-500 transition-colors hover:text-gray-700"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </Link>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => handleAddRithmo({ withBrief: false })}
                disabled={isFinalizing || isSendingTurn}
                className="rounded-full border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 inline-flex items-center gap-2"
              >
                {isFinalizing && <Loader2 className="w-4 h-4 animate-spin" />}
                Add Rithmo without briefing
              </button>
              <button
                type="button"
                onClick={() => handleAddRithmo({ withBrief: true })}
                disabled={isFinalizing || isSendingTurn || !hasUserMessage}
                className="inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-semibold text-white transition disabled:cursor-not-allowed disabled:opacity-50"
                style={{
                  background:
                    isFinalizing || isSendingTurn || !hasUserMessage
                      ? '#9ca3af'
                      : 'linear-gradient(135deg, #16c1b2 0%, #0b6fa4 100%)',
                }}
              >
                {isFinalizing && <Loader2 className="w-4 h-4 animate-spin" />}
                Add Rithmo to This Meeting
              </button>
            </div>
          </div>

          <SetupChatShell
            rightPanel={
              hasUserMessage && fieldCompleteness ? (
                <BriefProgressPanel
                  fieldCompleteness={fieldCompleteness}
                  brief={brief}
                  agenda={sidebandAgenda}
                  emailChecks={null}
                  onDiscard={() => navigate(`/join-external?edit=${encodeURIComponent(meetingId)}`)}
                />
              ) : null
            }
          >
            <div className="flex h-full flex-col">
              <div className="flex-1 overflow-y-auto px-4 py-6 sm:px-6">
                <SetupMessageTimeline messages={messages}>
                  {/* Empty children — Timeline renders messages directly */}
                </SetupMessageTimeline>
              </div>
              <div className="border-t border-gray-100 bg-white px-4 py-3 sm:px-6">
                <SetupComposer
                  onSend={(content, opts) => handleSendMessage(content, opts || {})}
                  disabled={isSendingTurn || isFinalizing}
                  placeholder="Tell Rithmo about this meeting…"
                />
              </div>
            </div>
          </SetupChatShell>
        </div>
      </main>
    </div>
  )
}
